import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import API from "../../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.min.css";
import { formatDateDDMMYYY as formatDate } from "../../utils/dateFormat";

const CampaignInfo = () => {
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [selectedBrand, setSelectedBrand] = useState(-1);
    const [selectedSalesPerson, setSelectedSalesPerson] = useState(-1);
    const [loader, setLoader] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [campaignInfo, setCampaignInfo] = useState([]);
    const [brands, setBrands] = useState([]);
    const [salesPersons, setSalesPersons] = useState([]);
    const [newsPaperData, setNewsPaperData] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const limit = 5;

    // Reference for the sentinel element
    const sentinelRef = useRef(null);

    const getLogo = (target) => {
        return newsPaperData.filter((item) => item.name === target)[0]?.logo;
    };

    const fetchCampaignInfo = async (reset = false) => {
        if (!hasMore && !reset) return;
        const currentPage = reset ? 1 : page;
        setLoader(!reset ? loadingMore : true);
        try {
            let reqObj = {
                brand_id: selectedBrand,
                month: selectedMonth,
                sales_person_id: selectedSalesPerson,
                page: currentPage,
                limit,
            };
            let res = await API({
                url: "/user/campaign_creation/getCampaignInfo",
                method: "POST",
                responseType: "json",
                data: reqObj,
            });
            if (res.code === 200) {
                const newData = res.data;
                setCampaignInfo((prev) => (reset ? newData : [...prev, ...newData]));
                setHasMore(newData.length === limit);
                if (!reset) setPage((prev) => prev + 1);
            }
        } catch (error) {
            console.error("Error fetching campaign info:", error);
        } finally {
            setLoader(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                if (selectedSalesPerson === -1) {
                    setBrands([]);
                    return;
                }
                let res = await API({
                    url: "/user/campaign_creation/getBrands",
                    method: "POST",
                    responseType: "json",
                    data: { sales_person_id: selectedSalesPerson },
                });
                if (res.code === 200) {
                    setBrands(res.data);
                }
            } catch (error) {
                console.error("Error fetching brands:", error);
            }
        };
        fetchBrands();
    }, [selectedSalesPerson]);

    useEffect(() => {
        const fetchSalesPersons = async () => {
            try {
                let res = await API({
                    url: "/user/campaign_creation/getSalesPersons",
                    method: "POST",
                    responseType: "json",
                });
                if (res.code === 200) {
                    setSalesPersons(res.data);
                }
            } catch (error) {
                console.error("Error fetching sales persons:", error);
            }
        };

        const fetchNewsPapers = async () => {
            try {
                let res = await API({
                    url: "/market/newspapers",
                    method: "POST",
                    responseType: "json",
                    data: {},
                });
                if (res.code === 200) {
                    setNewsPaperData([...res.data]);
                }
            } catch (error) {
                console.error("Error fetching newspapers:", error);
            }
        };
        fetchNewsPapers();
        fetchSalesPersons();
    }, []);

    useEffect(() => {
        setPage(1);
        setHasMore(true);
        setCampaignInfo([]);
        fetchCampaignInfo(true);
    }, [selectedMonth, selectedBrand, selectedSalesPerson]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loader && !loadingMore && hasMore) {
                    setLoadingMore(true);
                    fetchCampaignInfo();
                }
            },
            {
                threshold: 0, 
            }
        );

        if (sentinelRef.current) {
            observer.observe(sentinelRef.current);
        }

        // Cleanup observer on unmount
        return () => {
            if (sentinelRef.current) {
                observer.unobserve(sentinelRef.current);
            }
        };
    }, [loader, loadingMore, hasMore]);

    // Prepare options for React Select
    const salesPersonOptions = [
        { value: -1, label: "All" },
        ...salesPersons.map((salesPerson) => ({
            value: salesPerson.sales_person_id,
            label: salesPerson.sales_person_name,
        })),
    ];

    const brandOptions = [
        { value: -1, label: "All" },
        ...brands.map((brand) => ({
            value: brand.brand_id,
            label: brand.brand_name,
        })),
    ];

    return (
        <div className="p-4">
            <div className="flex justify-between flex-wrap items-center mb-4">
                <h3 className="whitespace-nowrap text-2xl font-bold">Campaign Information</h3>
                <div className="flex gap-3 items-center">
                    {/* Sales Person Dropdown */}
                    <Select
                        placeholder="Select Sales Person"
                        value={salesPersonOptions.find((option) => option.value === selectedSalesPerson)}
                        options={salesPersonOptions}
                        onChange={(selectedOption) => {
                            setSelectedSalesPerson(selectedOption.value);
                            setSelectedBrand(-1); // Reset brand when sales person changes
                        }}
                        styles={{
                            control: (provided) => ({
                                ...provided,
                                minWidth: "200px",
                            }),
                        }}
                    />

                    {/* Brand Dropdown */}
                    <Select
                        placeholder="Select Brand"
                        value={brandOptions.find((option) => option.value === selectedBrand) || null}
                        options={brandOptions}
                        onChange={(selectedOption) => setSelectedBrand(selectedOption.value)}
                        styles={{
                            control: (provided) => ({
                                ...provided,
                                minWidth: "200px",
                            }),
                        }}
                    />

                    {/* Date Picker */}
                    <div className="m-2">
                        <label className="form-label">Select Month</label>
                        <DatePicker
                            showMonthYearPicker
                            className="form-control border rounded p-2"
                            selected={selectedMonth}
                            onChange={setSelectedMonth}
                            dateFormat="MM/yyyy"
                        />
                    </div>
                </div>
            </div>

            {/* Loader */}
            {loader && (
                <div className="loader-container text-center py-4">
                    <span>Loading...</span>
                </div>
            )}

            {/* Display Campaign Cards */}
            {!loader && campaignInfo.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {campaignInfo.map((campaign, ind) => (
                        <div
                            className="rounded-lg border shadow-lg p-4 flex flex-col gap-2 bg-white"
                            key={ind}
                        >
                            <div className="flex flex-col font-semibold text-gray-700 items-start justify-center gap-2">
                                <div className="flex gap-2 text-xl bg-slate-300 rounded-md shadow-lg flex-wrap p-2">
                                    <span>Brand: </span>
                                    <span>{campaign.brand_name}</span>
                                </div>
                                <div className="flex gap-2 text-lg">
                                    <span>{campaign.sales_person_name}</span>
                                </div>
                            </div>
                            <div className="flex justify-between items-center">
                                <div className="font-semibold">
                                    <span className="text-sm text-gray-700">Campaign Expected: </span>
                                    <span>{formatDate(campaign.campaign_date)}</span>
                                </div>
                                <div className="font-semibold">
                                    <span className="text-sm text-gray-700">Info Given: </span>
                                    <span>{formatDate(campaign.reg_date)}</span>
                                </div>
                            </div>
                            <div className="flex gap-3 items-center">
                                <div className="font-semibold text-gray-700">
                                    <span className="text-sm">Target Platforms: </span>
                                    <div className="flex gap-2">
                                        {campaign.target_platform?.map((target) => (
                                            <img
                                                key={target}
                                                src={getLogo(target)}
                                                alt="logo"
                                                style={{ maxWidth: "50px", height: "auto" }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                !loader && (
                    <div className="no-data text-center py-4">
                        <h3>No Campaign Data Found</h3>
                    </div>
                )
            )}

            {/* Sentinel for Infinite Scroll */}
            {hasMore && !loader && (
                <div ref={sentinelRef} className="text-center py-4">
                    {loadingMore ? <span>Loading more...</span> : <span>Scroll to load more</span>}
                </div>
            )}
        </div>
    );
};

export default CampaignInfo;